import { useEffect, useState } from 'react'
import { useToastStore, type ToastType } from '@/stores/useToastStore'
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react'

const iconMap: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 className="h-5 w-5 text-green-600" />,
  error: <AlertCircle className="h-5 w-5 text-red-600" />,
  warning: <AlertTriangle className="h-5 w-5 text-amber-600" />,
  info: <Info className="h-5 w-5 text-blue-600" />,
}

const bgColorMap: Record<ToastType, string> = {
  success: 'bg-white border-[#E7E7E7]',
  error: 'bg-white border-[#E7E7E7]',
  warning: 'bg-white border-[#E7E7E7]',
  info: 'bg-white border-[#E7E7E7]',
}

const textColorMap: Record<ToastType, string> = {
  success: 'text-gray-900',
  error: 'text-gray-900',
  warning: 'text-gray-900',
  info: 'text-gray-900',
}

function useIsDrawerOpen() {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const check = () =>
      setIsOpen(!!document.querySelector('[data-slot="sheet-overlay"]'))

    check()

    const observer = new MutationObserver(check)
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  return isOpen
}

export function Toaster() {
  const toasts = useToastStore(state => state.toasts)
  const removeToast = useToastStore(state => state.removeToast)
  const isDrawerOpen = useIsDrawerOpen()

  if (toasts.length === 0) {
    return null
  }

  return (
    <div
      className={`fixed right-4 z-100 flex flex-col gap-3 max-w-md ${isDrawerOpen ? 'top-4' : 'bottom-4'}`}
    >
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`flex items-center gap-3 py-3 px-4 rounded-[6px] border shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] animate-in slide-in-from-right-full duration-300 ${bgColorMap[toast.type]}`}
          role="alert"
        >
          <div className="shrink-0">{iconMap[toast.type]}</div>
          <div
            className={`flex-1 text-sm font-medium leading-relaxed ${textColorMap[toast.type]}`}
          >
            {toast.message}
          </div>
          <button
            onClick={() => removeToast(toast.id)}
            className="shrink-0 text-gray-500 hover:text-gray-700 cursor-pointer transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  )
}
